import React, { useEffect } from 'react';

import { useIntl } from 'react-intl';

import { DatePicker, Drawer, Form, Input, message, Select, Switch } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';

import countries from '@src/util/countries.json';

import BoxContainer from 'components/BoxContainer';
import Title from 'components/BoxContainer/components/Title';
import FilterContainer from 'components/FilterContainer';

import { useHolidays } from '../../context';

/**
 * @description Drawer to create/edit holidays
 * @param {Object} props - Component props
 * @param {Boolean} props.visible - If the drawer is visible
 * @param {Function} props.onClose - Function to close the drawer
 * @param {Object} props.holiday - Holiday to edit (optional)
 * @param {String} props.mode - 'create' o 'edit'
 */
const HolidayDrawer = ({ visible, onClose, holiday = null, mode = 'create' }) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const { createHoliday, updateHoliday } = useHolidays();
  const isEditMode = mode === 'edit';

  useEffect(() => {
    if (visible && holiday && isEditMode) {
      const date = holiday.holiday.date ? moment(holiday.holiday.date) : null;
      form.setFieldsValue({
        name: holiday.holiday.name,
        date,
        active: holiday.active !== undefined ? holiday.active : true,
        id: holiday.id,
        country: holiday.holiday.country,
        region: holiday.holiday.region,
        isOptional: holiday.holiday.isOptional,
      });
    } else if (visible && !isEditMode) {
      form.resetFields();
      form.setFieldsValue({
        active: true,
      });
    }
  }, [visible, mode, holiday, isEditMode, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      let holidayData = {
        holiday: {
          name: values.name,
          date: values.date ? values.date : null,
          country: values.country,
          region: values.region,
          isOptional: !!values.isOptional,
        },
        active: values.active,
      };

      if (isEditMode) {
        holidayData = { ...holidayData, id: holiday.id };
        await updateHoliday(holiday.id, holidayData);
        message.success(intl.formatMessage({ id: 'holiday.messages.updateSuccess' }));
      } else {
        await createHoliday(holidayData);
        message.success(intl.formatMessage({ id: 'holiday.messages.createSuccess' }));
      }
      handleClose();
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      message.error(intl.formatMessage({ id: 'holiday.messages.saveError' }));
    }
  };

  return (
    <Drawer
      forceRender
      title={null}
      width={600}
      open={visible}
      closable={false}
      bodyStyle={{ paddingBottom: 80 }}
    >
      <BoxContainer>
        <BoxContainer fullContent shadow fixed>
          <FilterContainer
            title={
              <Title.Header
                value={
                  isEditMode
                    ? intl.formatMessage({ id: 'holiday.drawer.editTitle' })
                    : intl.formatMessage({ id: 'holiday.drawer.createTitle' })
                }
              />
            }
            buttonItems={[
              {
                iconName: 'save',
                action: handleSubmit,
              },
            ]}
            goBack={handleClose}
          />
        </BoxContainer>
        <BoxContainer fullContent>
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              active: true,
            }}
          >
            <Form.Item
              name="name"
              label={intl.formatMessage({ id: 'holiday.form.name' })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'holiday.form.nameRequired' }),
                },
              ]}
            >
              <Input placeholder={intl.formatMessage({ id: 'holiday.form.namePlaceholder' })} />
            </Form.Item>

            <Form.Item
              name="date"
              label={intl.formatMessage({ id: 'holiday.form.date' })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'holiday.form.dateRequired' }),
                },
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder={intl.formatMessage({ id: 'holiday.form.datePlaceholder' })}
                format="DD/MM/YYYY"
              />
            </Form.Item>

            <Form.Item
              name="country"
              label={intl.formatMessage({ id: 'holiday.form.country' })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'holiday.form.countryRequired' }),
                },
              ]}
            >
              <Select
                placeholder={intl.formatMessage({ id: 'holiday.form.countryPlaceholder' })}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {countries.map(country => (
                  <Select.Option key={country.name.common} value={country.name.common}>
                    {country.spanishName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="active"
              label={intl.formatMessage({ id: 'holiday.form.active' })}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Form>
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};

HolidayDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  holiday: PropTypes.object,
  mode: PropTypes.oneOf(['create', 'edit']),
};

export default HolidayDrawer;
